import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  tabsConfig,
  cabinaMensajes,
  cabinaEncuestas,
  cabinaConcursos,
  cabinaTopFans,
  cabinaStats,
  moderacionConfig,
  locutorActual,
  programaActual,
  reglasCabina,
  cabinaCoverImage,
} from '@/mocks/cabina2';
import type { CabinaTab, CabinaMessage } from '@/mocks/cabina2';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  return `Hace ${Math.floor(mins / 60)}h`;
}

const typeBadge: Record<string, { label: string; bg: string; text: string }> = {
  mensaje: { label: 'Mensaje', bg: 'bg-foreground-100', text: 'text-foreground-600' },
  saludo: { label: 'Saludo', bg: 'bg-rose-100', text: 'text-rose-700' },
  cancion: { label: 'Canción', bg: 'bg-violet-100', text: 'text-violet-700' },
  voz: { label: 'Voz', bg: 'bg-sky-100', text: 'text-sky-700' },
  concurso_respuesta: { label: 'Concurso', bg: 'bg-amber-100', text: 'text-amber-700' },
  encuesta_voto: { label: 'Encuesta', bg: 'bg-emerald-100', text: 'text-emerald-700' },
};

export default function Cabina2Page() {
  const [activeTab, setActiveTab] = useState<CabinaTab>('chat');
  const [showModPanel, setShowModPanel] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<CabinaMessage | null>(null);
  const [showReglas, setShowReglas] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);

  const mensajesFiltrados = cabinaMensajes.filter((m) => {
    if (activeTab === 'chat') return (m.type === 'mensaje' || m.type === 'voz') && !m.bloqueado;
    if (activeTab === 'saludos') return m.type === 'saludo' && !m.bloqueado;
    if (activeTab === 'canciones') return m.type === 'cancion' && !m.bloqueado;
    if (activeTab === 'concursos') return m.type === 'concurso_respuesta' && !m.bloqueado;
    if (activeTab === 'encuestas') return m.type === 'encuesta_voto' && !m.bloqueado;
    return true;
  });

  const tabCounts: Record<CabinaTab, number> = {
    chat: cabinaMensajes.filter((m) => m.type === 'mensaje' || m.type === 'voz').length,
    saludos: cabinaMensajes.filter((m) => m.type === 'saludo').length,
    canciones: cabinaMensajes.filter((m) => m.type === 'cancion').length,
    encuestas: cabinaEncuestas.filter((e) => e.activa).length,
    concursos: cabinaConcursos.filter((c) => c.activo).length,
    'top-fans': cabinaTopFans.length,
  };

  const handleModAction = (action: string) => {
    setShowModPanel(false);
    setSelectedMessage(null);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
        <img
          src={cabinaCoverImage}
          alt="Cabina de Radio"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/20 backdrop-blur-sm border border-rose-400/30 text-rose-200 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse"></span>
            CABINA 2.0
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            El Corazón de RFW
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-[560px] leading-relaxed">
            Chat, saludos, canciones, encuestas, concursos y top fans. Todo en un solo lugar. Moderación con atajos de teclado.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {cabinaStats.oyentesAhora.toLocaleString()} oyentes ahora
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {cabinaStats.mensajesPorMinuto} msgs/min
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {cabinaStats.vipsEnCabina} VIP
            </span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4 overflow-x-auto">
            <div className="flex items-center gap-6 shrink-0">
              <Link to="/" className="flex items-center gap-1.5 text-foreground-400 hover:text-foreground-700 transition-colors">
                <i className="ri-arrow-left-line"></i>
                <span className="text-xs font-semibold">RFW</span>
              </Link>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <img src={locutorActual.avatar} alt={locutorActual.nombre} className="w-8 h-8 rounded-lg object-cover" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-background-50"></span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-foreground-950 font-bold text-xs">{locutorActual.nombre}</p>
                  <p className="text-foreground-400 text-[10px]">{locutorActual.programa}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-[10px] shrink-0">
              <span className="text-foreground-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>{cabinaStats.oyentesAhora.toLocaleString()}
              </span>
              <span className="text-foreground-500 flex items-center gap-1">
                <i className="ri-vip-crown-line text-amber-500"></i>{cabinaStats.vipsEnCabina}
              </span>
              <span className="text-foreground-500 flex items-center gap-1">
                <i className="ri-shield-star-line text-violet-500"></i>{cabinaStats.embajadoresEnCabina}
              </span>
              <span className="text-foreground-500 hidden md:inline">{programaActual.horario}</span>
              <span className="text-foreground-400 hidden md:inline">Faltan {programaActual.tiempoRestante}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
        <div className="flex gap-6">
          {/* Main Panel */}
          <div className="flex-1 min-w-0">
            {/* Tabs */}
            <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1">
              {tabsConfig.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
                  }`}
                >
                  <i className={tab.icono}></i>
                  {tab.label}
                  <span className={`text-[10px] ml-0.5 ${activeTab === tab.id ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {tabCounts[tab.id]}
                  </span>
                </button>
              ))}
              <button
                onClick={() => setShowReglas(!showReglas)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  showReglas
                    ? 'bg-rose-500 text-background-50'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
                }`}
              >
                <i className="ri-scales-line"></i>
                Reglas
              </button>
            </div>

            {/* Reglas Panel */}
            {showReglas && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reglasCabina.map((regla) => (
                  <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                        <i className={`${regla.icono} text-background-50 text-sm`}></i>
                      </div>
                      <div>
                        <h4 className="text-foreground-950 font-bold text-xs mb-1">{regla.titulo}</h4>
                        <p className="text-foreground-500 text-[11px] leading-relaxed">{regla.descripcion}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Content */}
            {activeTab === 'top-fans' ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {cabinaTopFans.map((fan, idx) => (
                  <div key={fan.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center hover:border-background-300 transition-colors">
                    <div className="relative inline-block mb-3">
                      <img src={fan.avatar} alt={fan.nombre} className="w-16 h-16 rounded-2xl object-cover" loading="lazy" />
                      <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-background-50 border-2 border-background-200 flex items-center justify-center text-[10px] font-bold text-foreground-950">
                        {idx + 1}
                      </span>
                    </div>
                    <p className="text-foreground-950 font-bold text-sm">{fan.nombre}</p>
                    <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold mt-1 ${
                      fan.nivel === 'Diamante' ? 'bg-rose-100 text-rose-700' :
                      fan.nivel === 'Oro' ? 'bg-amber-100 text-amber-700' :
                      fan.nivel === 'Plata' ? 'bg-sky-100 text-sky-700' :
                      'bg-amber-700/10 text-amber-700'
                    }`}>
                      <i className={`${fan.insignia} mr-1`}></i>{fan.nivel}
                    </span>
                    <div className="flex items-center justify-center gap-4 mt-2">
                      <span className="text-foreground-400 text-[10px]">{fan.mensajes.toLocaleString()} msgs</span>
                      <span className="text-foreground-400 text-[10px]">{fan.racha}d racha</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : activeTab === 'encuestas' ? (
              <div className="space-y-5">
                {cabinaEncuestas.map((enc) => (
                  <div key={enc.id} className={`bg-background-50 rounded-2xl border ${enc.activa ? 'border-background-200' : 'border-background-100 opacity-75'} p-5`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-foreground-950 font-bold text-sm">{enc.pregunta}</p>
                        <p className="text-foreground-400 text-[10px]">Creada por {enc.creadaPor}</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${enc.activa ? 'text-emerald-700 bg-emerald-100' : 'text-foreground-500 bg-foreground-100'}`}>
                        {enc.tiempoRestante}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {enc.opciones.map((op) => {
                        const pct = enc.totalVotos > 0 ? Math.round((op.votos / enc.totalVotos) * 100) : 0;
                        return (
                          <div key={op.id}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-foreground-600 font-medium">{op.texto}</span>
                              <span className="text-foreground-400">{op.votos.toLocaleString()} ({pct}%)</span>
                            </div>
                            <div className="h-2.5 bg-background-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-accent-500 rounded-full transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-foreground-400 text-[10px] mt-3">{enc.totalVotos.toLocaleString()} votos totales</p>
                  </div>
                ))}
              </div>
            ) : activeTab === 'concursos' ? (
              <div className="space-y-5">
                {cabinaConcursos.map((con) => (
                  <div key={con.id} className={`bg-background-50 rounded-2xl border ${con.activo ? 'border-background-200' : 'border-background-100 opacity-75'} p-5`}>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <p className="text-foreground-950 font-bold text-sm">{con.titulo}</p>
                        <p className="text-amber-600 text-xs font-semibold flex items-center gap-1 mt-0.5">
                          <i className="ri-trophy-line"></i>{con.premio}
                        </p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md shrink-0 ${con.activo ? 'text-emerald-700 bg-emerald-100' : 'text-foreground-500 bg-foreground-100'}`}>
                        {con.tiempoRestante}
                      </span>
                    </div>
                    <p className="text-foreground-600 text-xs mb-3">"{con.pregunta}"</p>
                    <div className="space-y-2">
                      {con.respuestas.map((resp, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                          <span className="text-foreground-400 text-[10px] font-mono shrink-0">#{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-foreground-950 text-xs font-medium">{resp.usuario}</p>
                            <p className="text-foreground-500 text-[10px] truncate">"{resp.respuesta}"</p>
                          </div>
                          <span className="text-foreground-400 text-[9px] shrink-0">{timeAgo(resp.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                    {con.ganador && (
                      <div className="mt-3 bg-amber-100 rounded-xl p-3 flex items-center gap-2">
                        <i className="ri-trophy-line text-amber-600"></i>
                        <span className="text-amber-800 text-xs font-bold">Ganador: {con.ganador}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Message Feed */
              <div className="space-y-3">
                {mensajesFiltrados.map((msg) => {
                  const badge = typeBadge[msg.type];
                  return (
                    <div
                      key={msg.id}
                      className={`bg-background-50 rounded-2xl border p-4 cursor-pointer transition-colors ${
                        msg.destacado ? 'border-amber-300 bg-amber-50/30' : 'border-background-200 hover:border-background-300'
                      } ${selectedMessage?.id === msg.id ? 'ring-2 ring-accent-400' : ''}`}
                      onClick={() => { setSelectedMessage(msg); setShowModPanel(true); }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative shrink-0">
                          <img src={msg.senderAvatar} alt={msg.senderName} className="w-10 h-10 rounded-xl object-cover" loading="lazy" />
                          {msg.esEmbajador && (
                            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-violet-500 rounded-full border border-background-50 flex items-center justify-center">
                              <i className="ri-shield-star-line text-background-50 text-[7px]"></i>
                            </span>
                          )}
                          {msg.esVIP && !msg.esEmbajador && (
                            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border border-background-50 flex items-center justify-center">
                              <i className="ri-vip-crown-line text-background-50 text-[7px]"></i>
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-foreground-950 font-bold text-xs">{msg.senderName}</span>
                            {msg.senderBadge && (
                              <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${
                                msg.senderBadge.includes('VIP') ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                              }`}>
                                {msg.senderBadge}
                              </span>
                            )}
                            <span className={`px-1.5 py-0.5 rounded-md ${badge.bg} ${badge.text} text-[9px] font-semibold`}>
                              {badge.label}
                            </span>
                            {msg.destacado && (
                              <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-semibold">
                                <i className="ri-star-fill mr-0.5"></i>Destacado
                              </span>
                            )}
                          </div>
                          <p className="text-foreground-600 text-xs leading-relaxed">{msg.content}</p>
                          {msg.songRequest && (
                            <p className="text-violet-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                              <i className="ri-music-fill"></i>{msg.songRequest}
                            </p>
                          )}
                          {msg.voiceDuration && (
                            <p className="text-sky-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                              <i className="ri-mic-fill"></i>Mensaje de voz — {msg.voiceDuration}
                            </p>
                          )}
                        </div>
                        <span className="text-foreground-400 text-[10px] shrink-0">{timeAgo(msg.timestamp)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="hidden lg:block w-[300px] shrink-0 space-y-4">
            {/* Locutor + Programa */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <img src={locutorActual.avatar} alt={locutorActual.nombre} className="w-12 h-12 rounded-xl object-cover" />
                <div>
                  <p className="text-foreground-950 font-bold text-sm">{locutorActual.nombre}</p>
                  <span className="px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold">{locutorActual.estado}</span>
                </div>
              </div>
              <p className="text-foreground-500 text-xs leading-relaxed">{locutorActual.bio}</p>
              <p className="text-foreground-400 text-[10px] mt-2">{locutorActual.cobertura} &bull; {locutorActual.seguidores.toLocaleString()} seguidores</p>
            </div>

            {/* Programa */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <h4 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                <i className="ri-calendar-event-line text-accent-500"></i>
                Programa Actual
              </h4>
              <p className="text-foreground-950 font-medium text-xs">{programaActual.nombre}</p>
              <p className="text-foreground-500 text-[10px]">{programaActual.categoria} &bull; {programaActual.radio}</p>
              <p className="text-foreground-400 text-[10px]">{programaActual.horario}</p>
              <div className="mt-2 h-1.5 bg-background-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${programaActual.progreso}%` }}></div>
              </div>
              <p className="text-foreground-400 text-[9px] mt-1">Faltan {programaActual.tiempoRestante}</p>
            </div>

            {/* Stats */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <h4 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                <i className="ri-bar-chart-2-line text-accent-500"></i>
                Stats en Vivo
              </h4>
              <div className="space-y-2">
                {[
                  { label: 'Oyentes Ahora', valor: cabinaStats.oyentesAhora.toLocaleString() },
                  { label: 'Pico Hoy', valor: cabinaStats.oyentesPico.toLocaleString() },
                  { label: 'Mensajes Hoy', valor: cabinaStats.mensajesHoy.toString() },
                  { label: 'Saludos Hoy', valor: cabinaStats.saludosHoy.toString() },
                  { label: 'Canciones Hoy', valor: cabinaStats.cancionesHoy.toString() },
                  { label: 'Msgs/min', valor: cabinaStats.mensajesPorMinuto.toString() },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <span className="text-foreground-500 text-[11px]">{stat.label}</span>
                    <span className="text-foreground-950 font-bold text-xs">{stat.valor}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Moderación */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <h4 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                <i className="ri-shield-flash-line text-accent-500"></i>
                Moderación
              </h4>
              <p className="text-foreground-400 text-[10px] mb-3">Seleccioná un mensaje del feed para activar la moderación</p>
              <div className="space-y-1.5">
                {moderacionConfig.map((acc) => (
                  <div key={acc.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${acc.color} flex items-center justify-center shrink-0`}>
                      <i className={`${acc.icono} text-background-50 text-xs`}></i>
                    </div>
                    <span className="text-foreground-600 text-xs font-medium">{acc.label}</span>
                    <span className="text-foreground-400 text-[9px] font-mono ml-auto">Tecla {acc.shortcut}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* VIP & Embajadores */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <h4 className="text-foreground-950 font-bold text-sm mb-3">En Cabina</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-[11px] flex items-center gap-1">
                    <i className="ri-vip-crown-line text-amber-500"></i>VIP
                  </span>
                  <span className="text-foreground-950 font-bold text-xs">{cabinaStats.vipsEnCabina}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-[11px] flex items-center gap-1">
                    <i className="ri-shield-star-line text-violet-500"></i>Embajadores
                  </span>
                  <span className="text-foreground-950 font-bold text-xs">{cabinaStats.embajadoresEnCabina}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-[11px] flex items-center gap-1">
                    <i className="ri-star-line text-amber-400"></i>Top Fans Activos
                  </span>
                  <span className="text-foreground-950 font-bold text-xs">{cabinaStats.topFansActivos}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mod Panel Modal */}
      {showModPanel && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setShowModPanel(false)}>
          <div className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-foreground-950 font-bold text-sm">Panel de Moderación</h3>
              <button onClick={() => setShowModPanel(false)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Selected message preview */}
            <div className="bg-background-100 rounded-xl p-4 mb-5">
              <div className="flex items-center gap-2 mb-1">
                <img src={selectedMessage.senderAvatar} alt={selectedMessage.senderName} className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-foreground-950 font-bold text-xs">{selectedMessage.senderName}</span>
                {selectedMessage.senderBadge && (
                  <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${
                    selectedMessage.senderBadge.includes('VIP') ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                  }`}>
                    {selectedMessage.senderBadge}
                  </span>
                )}
              </div>
              <p className="text-foreground-600 text-xs">{selectedMessage.content}</p>
              <p className="text-foreground-400 text-[9px] mt-1">{timeAgo(selectedMessage.timestamp)}</p>
            </div>

            {/* Mod actions grid */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              {moderacionConfig.map((acc) => (
                <button
                  key={acc.id}
                  onClick={() => {
                    if (acc.id === 'responder') {
                      setShowReply(true);
                    } else {
                      handleModAction(acc.id);
                    }
                  }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl bg-gradient-to-br ${acc.color} text-background-50 cursor-pointer hover:opacity-90 transition-opacity`}
                >
                  <i className={`${acc.icono} text-lg`}></i>
                  <span className="text-[10px] font-bold">{acc.label}</span>
                </button>
              ))}
            </div>

            {/* Reply input */}
            {showReply && (
              <div className="space-y-3">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Escribí tu respuesta..."
                  maxLength={500}
                  className="w-full h-24 rounded-xl border border-background-200 bg-background-100 p-3 text-xs text-foreground-950 placeholder:text-foreground-400 resize-none focus:outline-none focus:ring-2 focus:ring-accent-400"
                ></textarea>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-400 text-[10px]">{replyText.length}/500</span>
                  <button
                    onClick={() => { setShowReply(false); setReplyText(''); handleModAction('responder'); }}
                    className="px-4 py-2 rounded-xl bg-emerald-500 text-background-50 text-xs font-bold cursor-pointer hover:bg-emerald-600 transition-colors whitespace-nowrap"
                  >
                    Responder al Aire
                  </button>
                </div>
              </div>
            )}

            <p className="text-foreground-400 text-[9px] text-center mt-4">
              Seleccioná una acción o cerrá este panel
            </p>
          </div>
        </div>
      )}

      {/* Mobile Bottom Bar - Moderación rápida */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background-50 border-t border-background-200 p-3">
        <div className="flex items-center gap-2 overflow-x-auto">
          {moderacionConfig.map((acc) => (
            <button
              key={acc.id}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold text-background-50 bg-gradient-to-r ${acc.color} cursor-pointer whitespace-nowrap shrink-0`}
            >
              <i className={`${acc.icono} text-[10px]`}></i>
              {acc.label}
            </button>
          ))}
        </div>
      </div>

      {/* Spacer for mobile bottom bar */}
      <div className="lg:hidden h-16"></div>
    </div>
  );
}